import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { Pagination, PageInfo } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useCrawlRuns } from '@/hooks/useProOps'
import { cn } from '@/lib/utils'
import type { CrawlRun } from '@/lib/pocketbase'

export function CrawlRunsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = parseInt(searchParams.get('page') || '1')

  const { data, isLoading } = useCrawlRuns({ page, perPage: 20 })

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  return (
    <div>
      <PageHeader
        title="รอบการดึงข้อมูล"
        description="ติดตามงานดึงข้อมูล"
      />

      {data && (
        <div className="mb-4">
          <PageInfo
            currentPage={data.page}
            pageSize={data.perPage}
            totalItems={data.totalItems}
          />
        </div>
      )}

      {isLoading && <LoadingState />}

      {data && data.items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon icon="solar:refresh-circle-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">ไม่พบรอบการดึงข้อมูล</h3>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((run) => (
              <CrawlRunCard key={run.id} run={run} />
            ))}
          </div>
          <div className="mt-6">
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  )
}

function CrawlRunCard({ run }: { run: CrawlRun }) {
  const getStatusBadge = (status: string) => {
    const configs: Record<string, { variant: any; icon: string }> = {
      running: { variant: 'info', icon: 'solar:refresh-circle-line-duotone' },
      completed: { variant: 'success', icon: 'solar:check-circle-line-duotone' },
      failed: { variant: 'danger', icon: 'solar:close-circle-line-duotone' },
      queued: { variant: 'warning', icon: 'solar:clock-circle-line-duotone' },
    }
    const config = configs[status] || configs.queued
    const statusLabels: Record<string, string> = {
      running: 'กำลังทำงาน',
      completed: 'เสร็จสิ้น',
      failed: 'ล้มเหลว',
      queued: 'รอคิว',
    }
    const label = statusLabels[status] || status

    return (
      <Badge variant={config.variant} size="sm">
        <Icon
          icon={config.icon}
          className={cn('w-3 h-3', status === 'running' && 'animate-spin')}
        />
        {label}
      </Badge>
    )
  }

  const duration = run.finished_at && run.started_at
    ? Math.round((new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000)
    : null

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {getStatusBadge(run.status)}
            <span className="font-mono text-sm text-slate-500">
              #{run.id.slice(0, 8)}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            <div>
              <p className="text-xs text-slate-500">แหล่งข้อมูล</p>
              <p className="font-medium text-slate-900">{run.source || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">ประกาศใหม่</p>
              <p className="font-medium text-slate-900">{run.tenders_created || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">อัปเดต</p>
              <p className="font-medium text-slate-900">{run.tenders_updated || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">ระยะเวลา</p>
              <p className="font-medium text-slate-900">
                {duration ? `${duration} วินาที` : '-'}
              </p>
            </div>
          </div>
          {run.error_message && (
            <p className="mt-2 text-sm text-rose-600 bg-rose-50 rounded-lg p-2">
              {run.error_message}
            </p>
          )}
        </div>
        <div className="text-right text-sm text-slate-500">
          <DateDisplay date={run.started_at} format="datetime" />
        </div>
      </div>
    </Card>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="animate-pulse">
          <div className="h-4 w-24 bg-slate-200 rounded" />
          <div className="grid grid-cols-4 gap-4 mt-3">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="space-y-2">
                <div className="h-3 w-12 bg-slate-200 rounded" />
                <div className="h-4 w-16 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  )
}
